import React from 'react';
import { Link } from 'react-router';
import { ReactComponent as ArrowRight } from '@rocco/icons/svg/arrow/arrow-right.svg';
import { Button as RoccoButton } from '@rocco/components/button';
import { VideoBackground } from '@rocco/ui/banner/components/VideoBackground';
import { useCTAHandlers } from '~/ui-rocco/hooks/use-home-cta-actions';
import { cn } from '@rocco/utils/cn';

export interface HomePageIntroProps {
    title: string;
    description: string;
    listItems: string[];
    CTAButton:
        | {
              text: string;
              link: string;
          }
        | {
              text: string;
              link: string;
              color: 'primary' | 'secondary' | 'transparent';
          }[];
    video?: {
        videoCover: string;
        videoMp4: string;
        videoWebM: string;
        autoplay: boolean;
    };
    openLogin: (url: string) => void;
    flexReverse?: boolean;
    overriteTracking?: () => void;
}

export const HomePageIntro = ({
    title,
    description,
    listItems,
    CTAButton,
    video,
    openLogin,
    flexReverse = true,
    overriteTracking,
}: HomePageIntroProps) => {
    const { handleCTAClick } = useCTAHandlers('HomePageIntro');
    const isCTAButtonArray = Array.isArray(CTAButton);
    const containerClasses = cn(
        'flex w-full items-start justify-between bg-white md:flex-row qhd:mx-auto qhd:max-w-[1920px]',
        flexReverse ? 'flex-col-reverse' : 'flex-col',
    );

    return (
        <section className={containerClasses}>
            <div className="w-full md:w-1/2">
                <div className="flex flex-col gap-12 px-6 py-20 md:p-8 md:pb-0 md:pl-[12.5rem] md:pt-[16rem]">
                    <div
                        className="text-[2.75rem] leading-[100%] tracking-[-0.125rem] md:text-20"
                        dangerouslySetInnerHTML={{ __html: title }}
                    />
                    <div>
                        <p className="text-base text-gray-700 md:text-5 md:leading-[130%] md:tracking-[0.025rem]">
                            {description}
                        </p>
                        {listItems ? (
                            <ul className="mt-6 flex flex-col gap-2.5 md:mt-7.5 md:gap-2">
                                {listItems.map((item, index) => (
                                    <li
                                        key={`intro-list-item-${index}`}
                                        className="flex items-center gap-4 md:gap-6.5"
                                    >
                                        <span className="flex size-5.5 shrink-0 items-center justify-center rounded-full bg-brick-50 text-[0.6875rem] font-medium">
                                            {index + 1}
                                        </span>
                                        <span className="text-base leading-[130%] tracking-[0.025rem] text-gray-700 md:text-5">
                                            {item}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        ) : null}
                    </div>

                    <div className="flex gap-2.5">
                        {isCTAButtonArray ? (
                            CTAButton.map((button) => (
                                <RoccoButton
                                    key={button.text}
                                    color={button.color}
                                    size={'larger'}
                                    asChild
                                >
                                    <Link
                                        to={button.link}
                                        onClick={(e) => {
                                            if (overriteTracking) {
                                                overriteTracking();
                                            } else {
                                                handleCTAClick();
                                            }
                                            e.preventDefault();
                                            openLogin(button.link);
                                        }}
                                    >
                                        {button.text}
                                        {button.color !== 'transparent' && (
                                            <ArrowRight className="ml-2.5 w-5" />
                                        )}
                                    </Link>
                                </RoccoButton>
                            ))
                        ) : (
                            <RoccoButton
                                color="primary"
                                size={'larger'}
                                asChild
                            >
                                <Link
                                    to={CTAButton.link}
                                    onClick={(e) => {
                                        if (overriteTracking) {
                                            overriteTracking();
                                        } else {
                                            handleCTAClick();
                                        }
                                        e.preventDefault();
                                        openLogin(CTAButton.link);
                                    }}
                                >
                                    {CTAButton.text}
                                </Link>
                            </RoccoButton>
                        )}
                    </div>
                </div>
            </div>
            <div className="flex h-full w-full items-center justify-center md:h-full md:w-1/2">
                <div className="aspect-414/467 md:aspect-960/1080 relative flex size-full h-full items-center justify-center text-sm text-gray-500">
                    {video?.videoCover ||
                    video?.videoMp4 ||
                    video?.videoWebM ? (
                        <VideoBackground
                            {...video}
                            overlay={false}
                            backgroundTheme="transparent"
                        />
                    ) : null}
                </div>
            </div>
        </section>
    );
};
